import React from 'react';
import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form';
import { Box, Chip, FormControl, FormHelperText } from '@mui/material';
import { darken, lighten } from '@mui/material/styles';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { useStacklineTheme } from '@stackline/ui';
import colors from 'src/utils/colors';

type Option = {
  value: string;
  label: string;
  disabled?: boolean;
};

type Props<T extends FieldValues> = {
  name: Path<T>;
  title: string;
  description: string;
  options: Option[];
  onSelectionChange?: (selectedValues: string[]) => void;
};

function ControlledMultiSelect<T extends FieldValues>({
  name,
  title,
  description,
  options,
  onSelectionChange
}: Props<T>) {
  const { control, watch } = useFormContext<T>();
  const theme = useStacklineTheme();

  // Watch the form field value for immediate updates
  const watchedValue = watch(name);
  const selectedValues = Array.isArray(watchedValue) ? watchedValue : [];

  return (
    <Flex flexDirection="column" gap={16}>
      <Flex flexDirection="column" gap={8}>
        <Text variant="h3" sx={{ fontWeight: 600 }}>
          {title}
        </Text>
        <Text variant="body2" sx={{ fontSize: '16px', fontWeight: 400, lineHeight: '24px' }}>
          {description}
        </Text>
      </Flex>

      <Controller
        control={control}
        name={name}
        render={({ field: { onChange }, fieldState: { error } }) => {
          const handleOptionToggle = (optionValue: string) => {
            const isSelected = selectedValues.includes(optionValue);
            const newValue = isSelected
              ? selectedValues.filter((v) => v !== optionValue)
              : [...selectedValues, optionValue];
            onChange(newValue);

            // Call the callback to notify parent of selection changes
            onSelectionChange?.(newValue);
          };

          return (
            <FormControl error={!!error} fullWidth>
              {/* Option Pills Grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  rowGap: '12px',
                  columnGap: '16px',
                  marginBottom: error ? '8px' : 0
                }}
              >
                {options.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  const isDisabled = option.disabled || false;

                  return (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onClick={isDisabled ? undefined : () => handleOptionToggle(option.value)}
                      variant="outlined"
                      color="default"
                      disabled={isDisabled}
                      sx={{
                        height: '40px',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: 400,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        border: isSelected
                          ? `1px solid ${colors.darkBlue}`
                          : `1px solid ${isDisabled ? colors.grey : colors.graysSecondary}`,
                        backgroundColor: isSelected
                          ? colors.darkBlue
                          : isDisabled
                          ? colors.veryLightGrey
                          : 'transparent',
                        color: isSelected ? 'white' : isDisabled ? colors.grey : colors.darkBlue,
                        opacity: isDisabled ? 0.7 : 1,
                        '&:hover': !isDisabled
                          ? {
                              backgroundColor: `${
                                isSelected ? lighten(colors.darkBlue, 0.05) : darken(colors.white, 0.05)
                              } !important`
                            }
                          : {},
                        '&:focus': !isDisabled
                          ? {
                              backgroundColor: `${
                                isSelected ? lighten(colors.darkBlue, 0.05) : darken(colors.white, 0.05)
                              } !important`
                            }
                          : {},
                        transition: 'all 0.2s ease-in-out'
                      }}
                    />
                  );
                })}
              </Box>

              {error && (
                <FormHelperText sx={{ color: theme.colors.error, marginTop: '8px' }}>{error.message}</FormHelperText>
              )}
            </FormControl>
          );
        }}
      />
    </Flex>
  );
}

export default ControlledMultiSelect;
